import { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import LeftFilterPanel from '../../../Components/layout/LeftPanel/LeftFilterPanel';
import RightDisplayPanel from '../../../Components/layout/RightPanel/RightDisplayPanel';
import ActionButtons from '../../../Components/common/Button/ActionButtons';
import { VIEW_TRANSACTION_OPTIONS, getDateRangeForOption, mapCorporationPeriodDates } from '../../../HelperFunctions/ViewTransaction/dateRangeOptions';
import { buildGeneratePayload } from '../../../Payloads/generatePayload';
import Loader from '../../../Components/Loader';
import {
    getManagementGroupsList,
    getCorporationDetails,
    getDepartments,
    getJobTitles,
    GetTimeAndAttendanceReport,
    GetMemorizedTimeAndAttendanceReport,
    getCorpAndPCListByMgntGrp,
} from '../../../api';

import { usePermissions } from '../../../Hooks';
import {
    getClientUrlFromUrl,
    getAuthIDFromUrl,
    getMenuIdFromUrl,
    getCorpIdFromUrl,
} from '../../../APIs/auth';


const tabOptions = [
    { label: 'View', value: 'view' },
];

const initialDates = getDateRangeForOption('5') || { fromDate: '2026-01-01', toDate: '2026-01-31' };

const initialFilters = {
    managementGroup: 'ALL',
    corporation: 'all',
    transactionView: '5',
    fromDate: initialDates.fromDate,
    toDate: initialDates.toDate,
    department: 'all',
    departmentName: 'ALL',
    jobTitle: 'all',
    jobTitleName: 'ALL',
};

const Customize = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clintUrl, authId, menuId, corpId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const finalMenuId = menuId || getMenuIdFromUrl();
    const { permissions, loading: permissionsLoading } = usePermissions(finalMenuId);

    // Clear history state on page unload so browser page reload starts fresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            try {
                if (window.history && window.history.replaceState) {
                    window.history.replaceState(null, '', window.location.href);
                }
            } catch (e) {
                console.error('Error clearing state on unload:', e);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const state = useMemo(() => location.state || {}, [location.state]);


    const getReportNavigationUrl = useCallback(
        (selectedCorpIds) => {
            const finalClintUrl = clintUrl || getClientUrlFromUrl();
            const finalAuthId = authId || getAuthIDFromUrl();
            const finalMenuId = menuId || getMenuIdFromUrl();
            let finalCorpId = corpId || getCorpIdFromUrl() || 'ALL';

            if (selectedCorpIds && selectedCorpIds.length > 0) {
                finalCorpId = selectedCorpIds[0].split('_')[0] || finalCorpId;
            }

            if (finalClintUrl && finalAuthId && finalMenuId) {
                return `/TimeandAttendance-report/${encodeURIComponent(finalClintUrl)}/${encodeURIComponent(
                    finalAuthId
                )}/${encodeURIComponent(finalMenuId)}/${encodeURIComponent(finalCorpId)}`;
            }
            return '/TimeandAttendance-report';
        },
        [clintUrl, authId, menuId, corpId]
    );


    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => state.filters || initialFilters);
    const [managementGroupOptions, setManagementGroupOptions] = useState([
        { DisplayText: 'ALL', ValueCode: 'ALL' },
    ]);
    const [reportType, setReportType] = useState(() => state.reportType || 'daily');

    const fetchMgmtGroups = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getManagementGroupsList();
            if (Array.isArray(data) && data.length > 0) {
                setManagementGroupOptions(data);
            }
        } catch (err) {
            console.error('Failed to load management groups from API:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch Management Groups directly from API on mount
    useEffect(() => {
        fetchMgmtGroups();
    }, [fetchMgmtGroups]);

    // Fetch Memorized Details directly from API on mount (only if no active navigation state)
    const fetchMemorizedDetails = useCallback(async () => {
        const finalMenuId = menuId || getMenuIdFromUrl() || '000000000000000000000000000000000000';
        try {
            const data = await GetMemorizedTimeAndAttendanceReport({ MenuId: finalMenuId });
            // console.log('--- TIME AND ATTENDANCE MEMORIZE GET DETAILS RESPONSE ---', data);

            if (data) {
                const item = Array.isArray(data) ? data[0] : (data.data || data.Data || data.result || data.Result || data);
                if (item) {
                    const rawTransType = item.ViewTransType ?? item.viewTransType ?? item.TransactionView;
                    const rawReportBy = item.ReportBy ?? item.Reportby ?? item.reportBy;
                    const customizeString = item.CustomizeString ?? item.customizeString;

                    let newFromDate = '';
                    let newToDate = '';
                    let newTransView = '5';

                    // 1. ViewTransType -> use date range filter according to the number
                    if (rawTransType !== undefined && rawTransType !== null && String(rawTransType).trim() !== '') {
                        newTransView = String(rawTransType).trim();
                        const dates = getDateRangeForOption(newTransView);
                        if (dates) {
                            newFromDate = dates.fromDate;
                            newToDate = dates.toDate;
                        }
                    }

                    // 2. ReportBy -> 1 = daily, 2 => weekly, 3 => monthly
                    let mappedReportType = 'daily';
                    if (String(rawReportBy) === '2') {
                        mappedReportType = 'weekly';
                    } else if (String(rawReportBy) === '3') {
                        mappedReportType = 'monthly';
                    } else {
                        mappedReportType = 'daily';
                    }
                    setReportType(mappedReportType);

                    // 3. CustomizeString -> 1st token: Department, 2nd token: Job Name, Remaining: Column names
                    let deptVal = 'all';
                    let deptNameVal = 'ALL';
                    let jobVal = 'all';
                    let jobNameVal = 'ALL';
                    let parsedSelectedFields = [];

                    if (customizeString && typeof customizeString === 'string') {
                        const rawParts = customizeString.split('%');
                        const parts = rawParts[0] === '' ? rawParts.slice(1) : rawParts;

                        // 1st string: Department (e.g. "Restaurant One")
                        if (parts.length >= 1 && parts[0] && parts[0].trim() !== '') {
                            const rawDept = parts[0].trim();
                            if (rawDept.toLowerCase() !== 'all') {
                                deptVal = rawDept;
                                deptNameVal = rawDept;
                            }
                        }

                        // 2nd string: Job Name (e.g. "Night Audit")
                        if (parts.length >= 2 && parts[1] && parts[1].trim() !== '') {
                            const rawJob = parts[1].trim();
                            if (rawJob.toLowerCase() !== 'all') {
                                jobVal = rawJob;
                                jobNameVal = rawJob;
                            }
                        }

                        // Remaining: Selected Columns (e.g. punchIn, punchOut, scheduledhrs, actualhrs, overtimehrs, variancehrs, status)
                        if (parts.length > 2) {
                            const rawCols = parts.slice(2);
                            parsedSelectedFields = rawCols
                                .map((col) => {
                                    const clean = col.toLowerCase().replace(/[^a-z0-9]/g, '');
                                    if (clean === 'punchin' || clean === 'in' || clean === 'punchinhrs') return 'punchIn';
                                    if (clean === 'punchout' || clean === 'out' || clean === 'punchouthrs') return 'punchOut';
                                    if (clean === 'scheduledhrs' || clean === 'scheduled' || clean === 'scheduledhours') return 'scheduledHrs';
                                    if (clean === 'actualhrs' || clean === 'actual' || clean === 'actualhours') return 'actualHrs';
                                    if (clean === 'overtimehrs' || clean === 'overtime' || clean === 'overtimehours') return 'overtimeHrs';
                                    if (clean === 'variancehrs' || clean === 'variance' || clean === 'variancehours') return 'varianceHrs';
                                    if (clean === 'varianceovertimehrs' || clean === 'varianceovertime' || clean === 'varianceot' || clean === 'variancehoursothrs') return 'varianceOvertimeHrs';
                                    if (clean === 'status') return 'status';
                                    if (clean === 'otpercent' || clean === 'otpercentage' || clean === 'ot') return 'otPercent';
                                    if (clean === 'otrisk' || clean === 'overtimerisk') return 'otRisk';
                                    return null;
                                })
                                .filter(Boolean);
                        }
                    }

                    setFilters((prev) => ({
                        ...prev,
                        transactionView: newTransView,
                        ...(newFromDate ? { fromDate: newFromDate } : {}),
                        ...(newToDate ? { toDate: newToDate } : {}),
                        department: deptVal,
                        departmentName: deptNameVal,
                        jobTitle: jobVal,
                        jobTitleName: jobNameVal,
                    }));

                    if (parsedSelectedFields.length > 0) {
                        setSelectedFields(parsedSelectedFields);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load memorized details:', err);
        }
    }, [menuId]);

    useEffect(() => {
        if (!state.filters) {
            fetchMemorizedDetails();
        }
    }, [state.filters, fetchMemorizedDetails]);

    // Auto-select corporation if corpId is present in URL / route params
    useEffect(() => {
        if (state.filters) return;
        const urlCorpId = corpId || getCorpIdFromUrl();
        if (
            urlCorpId &&
            urlCorpId !== '000000000000000000000000000000000000' &&
            urlCorpId.toLowerCase() !== 'all' &&
            urlCorpId !== 'null' &&
            urlCorpId !== 'undefined'
        ) {
            const initCorporation = async () => {
                try {
                    const data = await getCorpAndPCListByMgntGrp('ALL');
                    if (Array.isArray(data) && data.length > 0) {
                        const matchedRecords = data.filter(
                            (item) =>
                                String(item.CorporationID || '').toLowerCase() === urlCorpId.toLowerCase() ||
                                String(item.id || '').toLowerCase().startsWith(urlCorpId.toLowerCase())
                        );
                        if (matchedRecords.length > 0) {
                            const matchedIds = matchedRecords.map((r) => r.id);
                            const singleRecord = matchedRecords[0];
                            const corpName =
                                singleRecord?.CorporationName ||
                                singleRecord?.dbaName ||
                                '1 Corporation Selected';

                            let datesUpdate = {};
                            try {
                                const details = await getCorporationDetails(urlCorpId);
                                if (details && details.PeriodStartDate) {
                                    const mapped = mapCorporationPeriodDates(details.PeriodStartDate, details.PeriodEndDate);
                                    if (mapped) {
                                        datesUpdate = {
                                            fromDate: mapped.fromDate,
                                            toDate: mapped.toDate,
                                            transactionView: '11',
                                        };
                                    }
                                }
                            } catch (e) {
                                console.error('Error fetching corporation details on mount in Customize:', e);
                            }

                            setFilters((prev) => ({
                                ...prev,
                                selectedCorporationIds: matchedIds,
                                selectedCorporationRecords: matchedRecords,
                                corporation: matchedIds.length > 1 ? `${matchedIds.length} Corporations Selected` : corpName,
                                ...datesUpdate,
                            }));
                        }
                    }
                } catch (err) {
                    console.error('Failed to auto-select corporation by URL corpId:', err);
                }
            };
            initCorporation();
        }
    }, [state.filters, corpId]);

    const isInitialMulti = Boolean(
        (state.filters?.selectedCorporationIds && state.filters.selectedCorporationIds.length > 1) ||
        (state.filters?.corporation &&
            state.filters.corporation.includes('Corporations Selected') &&
            parseInt(state.filters.corporation, 10) > 1)
    );

    const [selectedFields, setSelectedFields] = useState(() => {
        const type = state.reportType || 'daily';
        if (state.selectedFields && state.selectedFields.length > 0) {
            if (type === 'daily') {
                if (!isInitialMulti) {
                    const hasVarOt = state.selectedFields.includes('varianceOvertimeHrs');
                    const filtered = state.selectedFields
                        .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'otPercent' && id !== 'otRisk');
                    if (hasVarOt) {
                        if (!filtered.includes('varianceHrs')) filtered.push('varianceHrs');
                        if (!filtered.includes('overtimeHrs')) filtered.push('overtimeHrs');
                    }
                    return filtered;
                } else {
                    const hasVarOt = state.selectedFields.includes('varianceOvertimeHrs') ||
                        state.selectedFields.includes('varianceHrs') ||
                        state.selectedFields.includes('overtimeHrs');
                    const filtered = state.selectedFields
                        .filter((id) => id !== 'status' && id !== 'overtimeHrs' && id !== 'varianceHrs');
                    if (hasVarOt && !filtered.includes('varianceOvertimeHrs')) {
                        filtered.push('varianceOvertimeHrs');
                    }
                    return filtered;
                }
            } else {
                const hasVarOt = state.selectedFields.includes('varianceOvertimeHrs');
                const filtered = state.selectedFields
                    .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'status' && id !== 'punchIn' && id !== 'punchOut');
                if (hasVarOt) {
                    if (!filtered.includes('varianceHrs')) filtered.push('varianceHrs');
                    if (!filtered.includes('overtimeHrs')) filtered.push('overtimeHrs');
                }
                return filtered.length > 0
                    ? filtered
                    : ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk'];
            }
        }
        return type === 'daily'
            ? !isInitialMulti
                ? ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'status']
                : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk']
            : ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk'];
    });
    const [selectedTab, setSelectedTab] = useState(() => state.selectedTab || 'view');
    const [displayFormat, setDisplayFormat] = useState(() => state.displayFormat || 'default');
    const [negativeFiguresFormat, setNegativeFiguresFormat] = useState(
        () => state.negativeFiguresFormat || 'normal'
    );
    const [withoutCents, setWithoutCents] = useState(() => Boolean(state.withoutCents));
    const [excludeZeroValues, setExcludeZeroValues] = useState(() => Boolean(state.excludeZeroValues));
    const [showNegativeInRed, setShowNegativeInRed] = useState(() => Boolean(state.showNegativeInRed));

    const lastHandledLocationKeyRef = useRef(location.key);

    // Sync state when navigating back with state
    useEffect(() => {
        if (!location.state || lastHandledLocationKeyRef.current === location.key) return;
        lastHandledLocationKeyRef.current = location.key;

        if (location.state.filters) {
            setFilters(location.state.filters);
        }
        if (location.state.reportType) {
            setReportType(location.state.reportType);
        }
        if (location.state.selectedFields) {
            const isBackMulti = Boolean(
                (location.state.filters?.selectedCorporationIds &&
                    location.state.filters.selectedCorporationIds.length > 1) ||
                (location.state.filters?.corporation &&
                    location.state.filters.corporation.includes('Corporations Selected') &&
                    parseInt(location.state.filters.corporation, 10) > 1)
            );
            const type = location.state.reportType || 'daily';
            let sanitized = [];
            if (type === 'daily') {
                if (!isBackMulti) {
                    const hasVarOt = location.state.selectedFields.includes('varianceOvertimeHrs');
                    sanitized = location.state.selectedFields
                        .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'otPercent' && id !== 'otRisk');
                    if (hasVarOt) {
                        if (!sanitized.includes('varianceHrs')) sanitized.push('varianceHrs');
                        if (!sanitized.includes('overtimeHrs')) sanitized.push('overtimeHrs');
                    }
                } else {
                    const hasVarOt = location.state.selectedFields.includes('varianceOvertimeHrs') ||
                        location.state.selectedFields.includes('varianceHrs') ||
                        location.state.selectedFields.includes('overtimeHrs');
                    sanitized = location.state.selectedFields
                        .filter((id) => id !== 'status' && id !== 'overtimeHrs' && id !== 'varianceHrs');
                    if (hasVarOt && !sanitized.includes('varianceOvertimeHrs')) {
                        sanitized.push('varianceOvertimeHrs');
                    }
                }
            } else {
                const hasVarOt = location.state.selectedFields.includes('varianceOvertimeHrs');
                sanitized = location.state.selectedFields
                    .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'status' && id !== 'punchIn' && id !== 'punchOut');
                if (hasVarOt) {
                    if (!sanitized.includes('varianceHrs')) sanitized.push('varianceHrs');
                    if (!sanitized.includes('overtimeHrs')) sanitized.push('overtimeHrs');
                }
                if (sanitized.length === 0) {
                    sanitized = ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk'];
                }
            }
            setSelectedFields(sanitized);
        }
        if (location.state.selectedTab) {
            setSelectedTab(location.state.selectedTab);
        }
        if (location.state.displayFormat) {
            setDisplayFormat(location.state.displayFormat);
        }
        if (location.state.negativeFiguresFormat) {
            setNegativeFiguresFormat(location.state.negativeFiguresFormat);
        }
        if (location.state.withoutCents !== undefined) {
            setWithoutCents(Boolean(location.state.withoutCents));
        }
        if (location.state.excludeZeroValues !== undefined) {
            setExcludeZeroValues(Boolean(location.state.excludeZeroValues));
        }
        if (location.state.showNegativeInRed !== undefined) {
            setShowNegativeInRed(Boolean(location.state.showNegativeInRed));
        }
    }, [location.key, location.state]);



    const isMultiCorp = useMemo(() => {
        if (filters?.selectedCorporationIds && filters.selectedCorporationIds.length > 1) return true;
        if (filters?.corporation && filters.corporation.includes('Corporations Selected')) {
            const count = parseInt(filters.corporation, 10);
            if (!isNaN(count) && count > 1) return true;
        }
        return false;
    }, [filters]);

    const reportTypeRef = useRef(reportType);
    reportTypeRef.current = reportType;

    const handleFilterChange = useCallback((key, value) => {
        if (key === 'selectedCorporationIds') {
            const isNowMulti = Array.isArray(value) && value.length > 1;
            if (reportTypeRef.current === 'daily') {
                setSelectedFields((prev) => {
                    if (!isNowMulti) {
                        const hasVarOt = prev.includes('varianceOvertimeHrs');
                        const filtered = prev
                            .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'otPercent' && id !== 'otRisk');
                        if (hasVarOt) {
                            if (!filtered.includes('varianceHrs')) filtered.push('varianceHrs');
                            if (!filtered.includes('overtimeHrs')) filtered.push('overtimeHrs');
                        }
                        return filtered.includes('status') ? filtered : [...filtered, 'status'];
                    } else {
                        const hasVarOrOt = prev.includes('varianceHrs') || prev.includes('overtimeHrs') || prev.includes('varianceOvertimeHrs');
                        const filtered = prev
                            .filter((id) => id !== 'status' && id !== 'overtimeHrs' && id !== 'varianceHrs');
                        if (hasVarOrOt && !filtered.includes('varianceOvertimeHrs')) {
                            filtered.push('varianceOvertimeHrs');
                        }
                        return filtered.length > 0
                            ? filtered
                            : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk'];
                    }
                });
            }
        }
        if (key === 'transactionView') {
            if (typeof value === 'string' && (value.startsWith('-2') || value.startsWith('separator'))) {
                setFilters((current) => ({
                    ...current,
                    transactionView: '3',
                    fromDate: '',
                    toDate: '',
                }));
                return;
            }
            const dates = getDateRangeForOption(value);
            if (dates) {
                setFilters((current) => ({
                    ...current,
                    transactionView: value,
                    fromDate: dates.fromDate,
                    toDate: dates.toDate,
                }));
                return;
            }
        }
        if (key === 'fromDate' || key === 'toDate') {
            setFilters((current) => ({
                ...current,
                [key]: value,
                transactionView: '3',
            }));
            return;
        }
        setFilters((current) => ({ ...current, [key]: value }));
    }, []);

    const handleReportTypeChange = useCallback(
        (value) => {
            setReportType(value);
            setSelectedFields((prev) => {
                if (value === 'daily') {
                    if (!isMultiCorp) {
                        const hasVarOt = prev.includes('varianceOvertimeHrs');
                        const filtered = prev
                            .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'otPercent' && id !== 'otRisk');
                        if (hasVarOt) {
                            if (!filtered.includes('varianceHrs')) filtered.push('varianceHrs');
                            if (!filtered.includes('overtimeHrs')) filtered.push('overtimeHrs');
                        }
                        const withPunch = filtered.includes('punchIn') ? filtered : ['punchIn', 'punchOut', ...filtered];
                        return withPunch.includes('status') ? withPunch : [...withPunch, 'status'];
                    } else {
                        const hasVarOrOt = prev.includes('varianceHrs') || prev.includes('overtimeHrs') || prev.includes('varianceOvertimeHrs');
                        const filtered = prev
                            .filter((id) => id !== 'status' && id !== 'overtimeHrs' && id !== 'varianceHrs');
                        if (hasVarOrOt && !filtered.includes('varianceOvertimeHrs')) {
                            filtered.push('varianceOvertimeHrs');
                        }
                        const withPunch = filtered.includes('punchIn') ? filtered : ['punchIn', 'punchOut', ...filtered];
                        return withPunch.length > 0
                            ? withPunch
                            : ['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'varianceOvertimeHrs', 'otPercent', 'otRisk'];
                    }
                } else {
                    const hasVarOt = prev.includes('varianceOvertimeHrs');
                    const filtered = prev
                        .filter((id) => id !== 'varianceOvertimeHrs' && id !== 'status' && id !== 'punchIn' && id !== 'punchOut');
                    if (hasVarOt) {
                        if (!filtered.includes('varianceHrs')) filtered.push('varianceHrs');
                        if (!filtered.includes('overtimeHrs')) filtered.push('overtimeHrs');
                    }
                    return filtered.length > 0
                        ? filtered
                        : ['scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'otPercent', 'otRisk'];
                }
            });
        },
        [isMultiCorp]
    );



    const handleToggleField = useCallback((fieldId) => {
        setSelectedFields((current) =>
            current.includes(fieldId) ? current.filter((item) => item !== fieldId) : [...current, fieldId],
        );
    }, []);

    const handleTabChange = useCallback((value) => {
        setSelectedTab(value);
    }, []);

    const handleDisplayFormatChange = useCallback((value) => {
        setDisplayFormat(value);
    }, []);

    const handleNegativeFiguresFormatChange = useCallback((value) => {
        setNegativeFiguresFormat(value);
    }, []);

    const handleToggleWithoutCents = useCallback(() => {
        setWithoutCents((prev) => !prev);
    }, []);

    const handleToggleExcludeZeroValues = useCallback(() => {
        setExcludeZeroValues((prev) => !prev);
    }, []);

    const handleToggleShowNegativeInRed = useCallback(() => {
        setShowNegativeInRed((prev) => !prev);
    }, []);

    const handleClear = useCallback(() => {
        setFilters(initialFilters);
        setReportType('daily');
        setSelectedFields(['punchIn', 'punchOut', 'scheduledHrs', 'actualHrs', 'overtimeHrs', 'varianceHrs', 'status']);
        setDisplayFormat('default');
        setNegativeFiguresFormat('normal');
        setWithoutCents(false);
        setExcludeZeroValues(false);
        setShowNegativeInRed(false);
    }, []);

    const handleGenerate = useCallback(async () => {
        if (!permissions.canView) {
            enqueueSnackbar("You don't have permission to generate or view this report.", {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
            return;
        }

        const hasSelectedCorp = Boolean(
            filters.selectedCorporationIds && filters.selectedCorporationIds.length > 0
        );

        if (!hasSelectedCorp) {
            enqueueSnackbar('Please select at least one corporation', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
            return;
        }

        const validSelectedFields =
            reportType === 'daily'
                ? isMultiCorp
                    ? selectedFields.filter((id) => id !== 'status')
                    : selectedFields.filter((id) => id !== 'otPercent' && id !== 'otRisk')
                : selectedFields.filter((id) => id !== 'status' && id !== 'punchIn' && id !== 'punchOut');

        let currentFilters = { ...filters };

        // Fallback: Resolve all department IDs if 'all' and not yet stored
        if (
            (!currentFilters.allDepartmentIds || currentFilters.allDepartmentIds.length === 0) &&
            (!currentFilters.department || String(currentFilters.department).toLowerCase() === 'all')
        ) {
            try {
                const depts = await getDepartments();
                currentFilters.allDepartmentIds = (depts || []).map((d) => d?.value).filter(Boolean);
            } catch (e) {
                console.error('Error resolving departments for generate payload:', e);
            }
        }

        // Fallback: Resolve all job title IDs if 'all' and not yet stored
        if (
            (!currentFilters.allJobTitleIds || currentFilters.allJobTitleIds.length === 0) &&
            (!currentFilters.jobTitle || String(currentFilters.jobTitle).toLowerCase() === 'all')
        ) {
            try {
                const jobs = await getJobTitles();
                currentFilters.allJobTitleIds = (jobs || []).map((j) => j?.value).filter(Boolean);
            } catch (e) {
                console.error('Error resolving job titles for generate payload:', e);
            }
        }

        const generatePayload = buildGeneratePayload({
            filters: currentFilters,
            reportType,
            selectedFields: validSelectedFields,
            fallbackCorpId: corpId,
        });

        // console.log('--- GENERATE PAYLOAD ---');
        // console.log(generatePayload);
        // console.log(JSON.stringify(generatePayload, null, 2));

        let reportData = null;
        setLoading(true);
        try {
            reportData = await GetTimeAndAttendanceReport(generatePayload);
            // console.log('--- TIME AND ATTENDANCE REPORT API RESPONSE ---', reportData);
        } catch (error) {
            console.error('Error fetching Time and Attendance Report:', error);
            enqueueSnackbar('Failed to fetch Time and Attendance Report', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } finally {
            setLoading(false);
        }

        const targetUrl = getReportNavigationUrl(currentFilters.selectedCorporationIds);
        navigate(targetUrl, {
            state: {
                filters: currentFilters,
                reportType,
                selectedFields: validSelectedFields,
                generatePayload,
                reportData,
                selectedTab,
                displayFormat,
                negativeFiguresFormat,
                withoutCents,
                excludeZeroValues,
                showNegativeInRed,
            },
        });
    }, [
        navigate,
        enqueueSnackbar,
        getReportNavigationUrl,
        filters,
        corpId,
        reportType,
        selectedFields,
        isMultiCorp,
        selectedTab,
        displayFormat,
        negativeFiguresFormat,
        withoutCents,
        excludeZeroValues,
        showNegativeInRed,
        permissions.canView,
    ]);

    const handleApplyAndGenerateCorporations = useCallback(
        async (selectedIds, selectedRecords) => {
            if (!permissions.canView) {
                enqueueSnackbar("You don't have permission to generate or view this report.", {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
                return;
            }

            if (!selectedIds || selectedIds.length === 0) {
                enqueueSnackbar('Please select at least one corporation', {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
                return;
            }

            let corpName = 'Corporations';
            let fromDate = filters.fromDate;
            let toDate = filters.toDate;
            let transactionView = filters.transactionView;

            if (selectedIds.length === 1) {
                const singleRecord = selectedRecords[0];
                corpName = singleRecord?.CorporationName || singleRecord?.dbaName || '1 Corporation Selected';
            } else if (selectedIds.length > 1) {
                corpName = `${selectedIds.length} Corporations Selected`;
            }

            const updatedFilters = {
                ...filters,
                corporation: corpName,
                selectedCorporationIds: selectedIds,
                selectedCorporationRecords: selectedRecords,
                department: 'all',
                departmentName: 'ALL',
                allDepartmentIds: [],
                allJobTitleIds: filters.allJobTitleIds || [],
                fromDate,
                toDate,
                transactionView,
            };

            const isNowMulti = Boolean(selectedIds && selectedIds.length > 1);

            const validSelectedFields =
                reportType === 'daily'
                    ? !isNowMulti
                        ? selectedFields.filter((id) => id !== 'otPercent' && id !== 'otRisk')
                        : selectedFields.filter((id) => id !== 'status')
                    : selectedFields.filter((id) => id !== 'status' && id !== 'punchIn' && id !== 'punchOut');

            const generatePayload = buildGeneratePayload({
                filters: updatedFilters,
                reportType,
                selectedFields: validSelectedFields,
                selectedRecords,
                fallbackCorpId: corpId,
            });

            const targetUrl = getReportNavigationUrl(selectedIds);
            navigate(targetUrl, {
                state: {
                    filters: updatedFilters,
                    reportType,
                    selectedFields: validSelectedFields,
                    generatePayload,
                    reportData: null,
                    selectedTab,
                    displayFormat,
                    negativeFiguresFormat,
                    withoutCents,
                    excludeZeroValues,
                    showNegativeInRed,
                },
            });
        },
        [
            navigate,
            enqueueSnackbar,
            getReportNavigationUrl,
            filters,
            corpId,
            reportType,
            selectedFields,
            selectedTab,
            displayFormat,
            negativeFiguresFormat,
            withoutCents,
            excludeZeroValues,
            showNegativeInRed,
            permissions.canView,
        ]
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 0 }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <ActionButtons
                        clearLabel="Clear"
                        generateLabel="Generate"
                        onClear={handleClear}
                        onGenerate={handleGenerate}
                    />
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        mt: 2,
                        gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' },
                        alignItems: 'start',
                    }}
                >
                    <LeftFilterPanel
                        filters={filters}
                        fromLabel="From"
                        toLabel="To"
                        transactionOptions={VIEW_TRANSACTION_OPTIONS}
                        managementGroupOptions={managementGroupOptions}
                        onFilterChange={handleFilterChange}
                        onApplyAndGenerateCorporations={handleApplyAndGenerateCorporations}
                    />
                    <RightDisplayPanel
                        tabs={tabOptions}
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                        selectedReportType={reportType}
                        reportByTitle="Report By"
                        onReportTypeChange={handleReportTypeChange}
                        selectedFields={selectedFields}
                        onToggleField={handleToggleField}
                        isMultiCorp={isMultiCorp}
                        selectedDisplayFormat={displayFormat}
                        onDisplayFormatChange={handleDisplayFormatChange}
                        selectedNegativeFiguresFormat={negativeFiguresFormat}
                        onNegativeFiguresFormatChange={handleNegativeFiguresFormatChange}
                        withoutCents={withoutCents}
                        onToggleWithoutCents={handleToggleWithoutCents}
                        excludeZeroValues={excludeZeroValues}
                        onToggleExcludeZeroValues={handleToggleExcludeZeroValues}
                        showNegativeInRed={showNegativeInRed}
                        onToggleShowNegativeInRed={handleToggleShowNegativeInRed}
                    />
                </Box>
            </Paper>
            <Loader loading={loading || permissionsLoading} />
        </Box>
    );
};

export default memo(Customize);
